import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { DateTime } from 'luxon';
import { AdvancedNewsManager } from './manager';
import {
  StoryCategory,
  Meta,
  Assets,
  NodeId,
  SocketStory,
  BaseQuote,
  Sentiment,
  StoryId,
  VersionId,
  Source,
  NewsfeedFieldType,
} from './entities';
import { StockSymbol } from '@benzinga/session';
import { findPrimaryImage } from './utils';

interface UpdateEvent {
  story: Story;
  type: 'story_update';
}

interface ExtendEvent {
  story: Story;
  type: 'story_extend';
}

interface BodyFetchEvent {
  story: Story;
  type: 'body_fetched';
}

export interface StoryObject {
  author: string | null | undefined;
  assets: Assets[];
  body: string | null | undefined;
  channels: StoryCategory[] | null | undefined;
  created: string;
  createdAt: Date;
  doNotDistribute: boolean | undefined;
  eventId: string | undefined;
  futures: StoryCategory[] | null | undefined;
  id: number;
  image: string;
  importanceRank: number | undefined;
  isBzPost: boolean | undefined;
  isBzProPost: boolean | undefined;
  meta: Meta | undefined;
  nodeId: number;
  partnerURL: string | null | undefined;
  published: boolean | undefined;
  quotes: BaseQuote[] | null | undefined;
  sentiment: Sentiment | undefined;
  stocks: StoryCategory[];
  storyId: string;
  tags: StoryCategory[];
  teaser: string | null | undefined;
  teaserText: string | null | undefined;
  tickers: StoryCategory[];
  title: string | null | undefined;
  updated: string;
  updatedAt: string;
  url: string;
  userId: number | undefined;
  versionId: number;
}

export type StoryEvent = UpdateEvent | ExtendEvent | BodyFetchEvent;

export class Story extends Subscribable<StoryEvent> {
  private addedAt: Date;
  private author?: string | null;
  private assets?: Assets[];
  private body?: string | null;
  private canonicalPath?: string | null;
  private channels?: StoryCategory[] | null;
  private createdAt: string;
  private createdAtDate: Date;
  private createdAtOffsetDate?: DateTime;
  private doNotDistribute?: boolean;
  private eventId?: string;
  private futures?: StoryCategory[] | null;
  private isBzPost?: boolean;
  private isBzProPost?: boolean;
  private meta?: Meta;
  private nodeId: NodeId;
  private partnerURL?: string | null;
  private published?: boolean;
  private quotes?: BaseQuote[] | null;
  private sentiment?: Sentiment;
  private importanceRank?: number;
  private storyId: StoryId;
  private tags?: StoryCategory[] | null;
  private teaserText?: string | null;
  private tickers?: StoryCategory[] | null;
  private title?: string | null;
  private source?: Source;
  private updatedAt: string;
  private updatedAtDate: Date;
  private userId?: number;
  private versionId: VersionId;

  private readonly manager: AdvancedNewsManager;

  constructor(story: SocketStory, manager: AdvancedNewsManager, sources: Source[] | undefined) {
    super();
    this.addedAt = new Date();
    this.assets = story.assets;
    this.author = story.name;
    this.body = story.Body;
    this.canonicalPath = story.CanonicalPath;
    this.channels = story.Channels;
    this.createdAt = story.CreatedAt;
    this.doNotDistribute = story.DoNotDistribute;
    this.eventId = story.EventID;
    this.futures = story.Futures;
    this.storyId = story.ID ?? null;
    this.isBzPost = story.IsBzPost;
    this.isBzProPost = story.IsBzProPost;
    this.meta = story.Meta;
    this.nodeId = story.NodeID;
    this.partnerURL = story.PartnerURL;
    this.published = story.Published;
    this.quotes = story.Quotes;
    this.sentiment = story.Sentiment;
    this.importanceRank = story.ImportanceRank;
    this.tags = story.Tags;
    this.teaserText = story.TeaserText;
    this.tickers = story.Tickers;
    this.title = story.Title;
    this.source = sources?.find(source => source.id === story.Type);
    this.updatedAt = story.UpdatedAt;
    this.userId = story.UserID;
    this.versionId = story.VersionID;

    this.createdAtDate = new Date(story.CreatedAt);
    this.updatedAtDate = new Date(story.UpdatedAt);

    this.manager = manager;
  }

  private static compareAndReturn = <T>(a: T, b: T, callback: (updated: boolean) => void): T => {
    callback(a === undefined && b !== undefined);
    return a ?? b;
  };

  public updateStory = (story: SocketStory, sources: Source[] | undefined): Story => {
    const updateType = this.storyId === story.ID ? 'story_extend' : 'story_update';

    if (updateType === 'story_update') {
      this.addedAt = new Date();
    }

    let didUpdate = false;
    const update = (updated: boolean): boolean => (didUpdate = didUpdate || updated);

    this.assets = Story.compareAndReturn(story.assets, this.assets, update);
    this.author = Story.compareAndReturn(story.name, this.author, update);
    this.body = Story.compareAndReturn(story.Body, this.body, update);
    this.canonicalPath = Story.compareAndReturn(story.CanonicalPath, this.canonicalPath, update);
    this.channels = Story.compareAndReturn(story.Channels, this.channels, update);
    this.createdAt = Story.compareAndReturn(story.CreatedAt, this.createdAt, update);
    this.doNotDistribute = Story.compareAndReturn(story.DoNotDistribute, this.doNotDistribute, update);
    this.eventId = Story.compareAndReturn(story.EventID, this.eventId, update);
    this.futures = Story.compareAndReturn(story.Futures, this.futures, update);
    this.storyId = Story.compareAndReturn(story.ID, this.storyId, update);
    this.isBzPost = Story.compareAndReturn(story.IsBzPost, this.isBzPost, update);
    this.isBzProPost = Story.compareAndReturn(story.IsBzProPost, this.isBzProPost, update);
    this.meta = Story.compareAndReturn(story.Meta, this.meta, update);
    this.nodeId = Story.compareAndReturn(story.NodeID, this.nodeId, update);
    this.partnerURL = Story.compareAndReturn(story.PartnerURL, this.partnerURL, update);
    this.published = Story.compareAndReturn(story.Published, this.published, update);
    this.quotes = Story.compareAndReturn(story.Quotes, this.quotes, update);
    this.sentiment = Story.compareAndReturn(story.Sentiment, this.sentiment, update);
    this.importanceRank = Story.compareAndReturn(story.ImportanceRank, this.importanceRank, update);
    this.tags = Story.compareAndReturn(story.Tags, this.tags, update);
    this.teaserText = Story.compareAndReturn(story.TeaserText, this.teaserText, update);
    this.tickers = Story.compareAndReturn(story.Tickers, this.tickers, update);
    this.title = Story.compareAndReturn(story.Title, this.title, update);
    this.updatedAt = Story.compareAndReturn(story.UpdatedAt, this.updatedAt, update);
    this.userId = Story.compareAndReturn(story.UserID, this.userId, update);
    this.versionId = Story.compareAndReturn(story.VersionID, this.versionId, update);

    const newSources = sources?.find(source => source.id === story.Type) ?? this.source;
    if (this.source !== newSources) {
      this.source = newSources;
      didUpdate = true;
    }
    this.createdAtDate = story.CreatedAt ? new Date(story.CreatedAt) : this.createdAtDate;
    this.updatedAtDate = story.UpdatedAt ? new Date(story.UpdatedAt) : this.updatedAtDate;

    if (didUpdate === true) {
      this.dispatch({
        story: this,
        type: updateType,
      });
    }

    return this;
  };

  public getBody = (): string | null | undefined => {
    return this.body;
  };

  public fetchBody = async (): SafePromise<string> => {
    const body = await this.manager.getStoryBody(this);
    if (!body.err) {
      this.body = body.ok;
    }
    this.dispatch({
      story: this,
      type: 'body_fetched',
    });
    return body;
  };

  public isBodyLoaded = (): boolean => this.body !== undefined;

  public getAddedAt = (): Date => {
    return this.addedAt;
  };

  public getAuthor = (): string | null | undefined => {
    return this.author;
  };

  public getAssets = (): Assets[] | undefined => {
    return this.assets;
  };

  public getChannels = (): StoryCategory[] | null | undefined => {
    return this.channels;
  };

  public getCreatedAt = (): string => {
    return this.createdAt;
  };

  public getCreatedAtDate = (): Date => {
    return this.createdAtDate;
  };

  public getCreatedAtOffsetDate = (): DateTime => {
    return this.createdAtOffsetDate ?? DateTime.fromJSDate(this.createdAtDate);
  };

  public getDisplayDateString = (): string => {
    return this.getCreatedAtOffsetDate().toFormat('cccc MMMM d, yyyy');
  };

  public setCreatedAtOffsetDate = (timeZone: string) => {
    this.createdAtOffsetDate = DateTime.fromJSDate(this.getCreatedAtDate()).setZone(timeZone);
  };

  public getDoNotDistribute = (): boolean | undefined => {
    return this.doNotDistribute;
  };

  public getEventId = (): string | undefined => {
    return this.eventId;
  };

  public getFutures = (): StoryCategory[] | null | undefined => {
    return this.futures;
  };

  public getIsBzPost = (): boolean | undefined => {
    return this.isBzPost;
  };

  public getIsBzProPost = (): boolean | undefined => {
    return this.isBzProPost;
  };

  public getMeta = (): Meta | undefined => {
    return this.meta;
  };

  public getNodeId = (): NodeId => {
    return this.nodeId;
  };

  public getPartnerURL = (): string | null | undefined => {
    return this.partnerURL;
  };

  public getPublished = (): boolean | undefined => {
    return this.published;
  };

  public getQuotes = (): BaseQuote[] | null | undefined => {
    return this.quotes;
  };

  public getSentiment = (): Sentiment | undefined => {
    return this.sentiment;
  };

  public getImportanceRank = (): number | undefined => {
    return this.importanceRank;
  };

  public getStoryId = (): StoryId => {
    return this.storyId;
  };

  public getTags = (): StoryCategory[] | null | undefined => {
    return this.tags;
  };

  public getTeaserText = (): string | null | undefined => {
    return this.teaserText;
  };

  public getTickers = (): StoryCategory[] | null | undefined => {
    return this.tickers;
  };

  public getTitle = (): string | null | undefined => {
    return this.title;
  };

  public getSource = (): Source | undefined => {
    return this.source;
  };

  public getUpdatedAt = (): string => {
    return this.updatedAt;
  };

  public getUpdatedAtDate = (): Date => {
    return this.updatedAtDate;
  };

  public getUserId = (): number | undefined => {
    return this.userId;
  };

  public getVersionId = (): VersionId => {
    return this.versionId;
  };

  public viewWithIframe = (): boolean => {
    const source = this.source;
    if (source) {
      return this.manager.getStoredPressReleases()?.includes(source.id) || this.source?.id === 'pr_secfilings';
    }
    return false;
  };

  public getStoryQuotes = (): StoryQuotes => {
    return (
      this.quotes?.reduce<StoryQuotes>((acc, item) => {
        acc[item.symbol] = item;
        return acc;
      }, {}) ?? {}
    );
  };

  public canonicalUrl = (): string => {
    let articleUrl = `${process.env.BASE_URL}/article/${this.nodeId}`;
    if (this.canonicalPath?.match(/https:\/\/[A-z]+.benzinga.com/)) {
      articleUrl = this.canonicalPath;
    } else {
      articleUrl = `${process.env.BASE_URL}/${this.canonicalPath?.trim()}`;
    }
    return articleUrl;
  };

  public toObject = (): Partial<StoryObject> => {
    return Object.entries({
      assets: this.assets,
      author: this.author,
      body: this.body,
      channels: this.channels,
      created: this.createdAt,
      createdAt: this.createdAt,
      doNotDistribute: this.doNotDistribute,
      eventId: this.eventId,
      futures: this.futures,
      id: this.nodeId,
      image: findPrimaryImage(this.assets),
      importanceRank: this.importanceRank,
      isBzPost: this.isBzPost,
      isBzProPost: this.isBzProPost,
      meta: this.meta,
      nodeId: this.nodeId,
      partnerURL: this.partnerURL,
      published: this.published,
      quotes: this.quotes,
      sentiment: this.sentiment,
      stocks: this.tickers ?? [],
      storyId: this.storyId,
      tags: this.tags ?? [],
      teaser: this.teaserText,
      teaserText: this.teaserText,
      tickers: this.tickers ?? [],
      title: this.title,
      updated: this.updatedAt,
      updatedAt: this.updatedAt,
      url: this.canonicalUrl(),
      versionId: this.versionId,
    }).reduce((acc, [key, value]) => {
      if (value !== undefined) acc[key] = value;
      return acc;
    }, {});
  };

  public hasFields = (fields: NewsfeedFieldType[]): boolean =>
    fields.every(field => {
      switch (field) {
        case 'assets':
          return this.assets !== undefined;
        case 'Body':
          return this.body !== undefined;
        case 'CanonicalPath':
          return this.canonicalPath !== undefined;
        case 'Channels':
          return this.channels !== undefined;
        case 'CreatedAt':
          return this.createdAt !== undefined;
        case 'DoNotDistribute':
          return this.doNotDistribute !== undefined;
        case 'EventID':
          return this.eventId !== undefined;
        case 'Futures':
          return this.futures !== undefined;
        case 'ID':
          return this.storyId !== undefined;
        case 'IsBzPost':
          return this.isBzPost !== undefined;
        case 'IsBzProPost':
          return this.isBzProPost !== undefined;
        case 'Meta':
          return this.meta !== undefined;
        case 'name':
          return this.author !== undefined;
        case 'NodeID':
          return this.nodeId !== undefined;
        case 'PartnerURL':
          return this.partnerURL !== undefined;
        case 'Published':
          return this.published !== undefined;
        case 'Quotes':
          return this.quotes !== undefined;
        case 'ImportanceRank':
          return this.importanceRank !== undefined;
        case 'Sentiment':
          return this.sentiment !== undefined;
        case `Tags`:
          return this.tags !== undefined;
        case 'TeaserText':
          return this.teaserText !== undefined;
        case `Tickers`:
          return this.tickers !== undefined;
        case 'Title':
          return this.title !== undefined;
        case 'Type':
          return this.source !== undefined;
        case 'UpdatedAt':
          return this.updatedAt !== undefined;
        case 'UserID':
          return this.userId !== undefined;
        case 'VersionID':
          return this.versionId !== undefined;
        default:
          return true;
      }
    });
}

export type StoryQuotes = {
  [stockSymbol in StockSymbol]: BaseQuote;
};
